import { useTranslation } from "react-i18next";
import type { LayerVersion } from "@/shared/types/layer";
import { Check, ChevronDown, Layers3 } from "@/shared/ui/icons";

export interface LayerVersionPickerProps {
  versions: LayerVersion[];
  current: LayerVersion;
  changed: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (version: LayerVersion) => void;
}

export function LayerVersionPicker({
  versions,
  current,
  changed,
  open,
  onOpenChange,
  onSelect,
}: LayerVersionPickerProps) {
  const { t, i18n } = useTranslation();
  const versionLabel = `${t("menu.version")} ${current.versionName.replace("v", "")}`;
  return (
    <div
      className="layer-version-dropdown"
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) onOpenChange(false);
      }}
    >
      <button
        type="button"
        className={`btn btn-sm h-9 gap-2 border-base-300 bg-base-100 px-3 text-secondary shadow-none hover:border-primary hover:bg-primary/5 ${changed ? "border-warning text-warning" : ""}`}
        aria-label={`Layer version: ${versionLabel}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => onOpenChange(!open)}
      >
        <span className="grid size-6 shrink-0 place-items-center rounded-md bg-primary" aria-hidden="true">
          <Layers3 className="size-4 text-white" />
        </span>
        <span>{versionLabel}</span>
        <ChevronDown
          className={`size-3.5 transition-transform ${open ? "rotate-180" : ""}`}
          aria-hidden="true"
        />
      </button>
      {open && (
        <div className="layer-version-menu" role="listbox" aria-label="Layer version">
          <div className="border-b border-base-300 px-3 py-2">
            <strong className="block text-[12px] text-base-content">Layer version</strong>
            <span className="text-[11px] text-muted">Select the runtime used by this workspace.</span>
          </div>
          <div className="max-h-80 overflow-auto p-1.5">
            {versions.map((version) => {
              const selected = version.versionName === current.versionName;
              return (
                <button
                  key={version.versionName}
                  type="button"
                  role="option"
                  aria-selected={selected}
                  className={`grid w-full grid-cols-[minmax(0,1fr)_auto] gap-3 rounded-lg px-3 py-2 text-left hover:bg-base-200 ${selected ? "bg-primary/10" : ""}`}
                  onClick={() => onSelect(version)}
                >
                  <span className="min-w-0">
                    <strong
                      className={`block text-[12px] ${selected ? "text-primary" : "text-base-content"}`}
                    >
                      {t("menu.version")} {version.versionName.replace("v", "")}
                    </strong>
                    {version.description?.[i18n.language] && (
                      <span className="mt-0.5 block line-clamp-2 text-[11px] leading-4 text-muted">
                        {version.description[i18n.language]}
                      </span>
                    )}
                  </span>
                  {selected && <Check className="mt-0.5 size-4 text-primary" aria-hidden="true" />}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
